<template>
    <modal-dialog
        ref="realworksTokenModal"
        description="Hier kun je de realworks tokens voor je vestiging instellen"
        label="Realworks token formulier"
        size="6"
    >
        <flex-container>
            <flex-container gap="sm" direction="column">
                <text-decorator bold :size="FontSize.xs">
                    Vestigingen
                </text-decorator>
                <text-heading>
                    Realworks koppeling
                </text-heading>
                <text-description>
                    Winck maakt het mogelijk om slimme marketingacties automatisch te starten op het moment dat er iets verandert in je Realworks data. Dit kan van alles zijn, zoals een wijziging in relatiestatus, het toevoegen van een kenmerk, of een verandering in afspraken, taken of statussen. <br><br>

                    Om dit te kunnen doen, moet je een speciale toegangssleutel (een 'token') aanvragen via de Realworks Marketplace. Deze vind je door in Realworks naar het onderdeel 'servicedesk' te gaan en daar naar de Marketplace te navigeren. Scroll naar beneden totdat je bij de API's komt, hier kun je de tokens vinden die Winck nodig heeft.<br><br>

                    <text-decorator bold>
                        Je hebt de volgende tokens nodig om alle functies van Winck te gebruiken:
                    </text-decorator>

                    <br>

                    <ul class="realworks-usage-list">
                        <li>
                            <svg-icon
                                :icon="CheckIcon"
                                :size="IconSize.sm"
                                is-custom-icon
                            />
                            Een relatie token
                        </li>
                        <li>
                            <svg-icon
                                :icon="CheckIcon"
                                :size="IconSize.sm"
                                is-custom-icon
                            />
                            Een agenda token
                        </li>
                        <li>
                            <svg-icon
                                :icon="CheckIcon"
                                :size="IconSize.sm"
                                is-custom-icon
                            />
                            Een taken token (als je met taken werkt)
                        </li>
                    </ul>

                    <flex-container
                        inline
                        no-wrap
                        align-items="center"
                    >
                        <span>
                            Tijdens het aanvragen geef je ons Realworks developer id door:
                        </span>
                        <flex-container :size="4">
                            <input-group model-value="5bfc47e9-5315-47c7-86f9-b7c8b659e1c6">
                                <template #input>
                                    <text-input readonly>
                                        <template #suffix>
                                            <button-default @click="writeText('5bfc47e9-5315-47c7-86f9-b7c8b659e1c6')">
                                                <svg-icon
                                                    :size="IconSize.sm"
                                                    :color="'gray-400'"
                                                    :icon="CopyIcon"
                                                    is-custom-icon
                                                />
                                            </button-default>
                                        </template>
                                    </text-input>
                                </template>
                            </input-group>
                        </flex-container>
                    </flex-container>

                    <br><br>
                    Na je aanvraag, laat het even weten aan je accountmanager. Hij of zij zal je helpen om het token correct in te stellen in Realworks, zodat je makkelijk aan de slag kunt met Winck.
                </text-description>
            </flex-container>

            <horizontal-line no-margin />

            <flex-container>
                <flex-container
                    :screen-sizes="{
                        laptop: 2,
                    }"
                >
                    <data-group>
                        <data-label>
                            Tokens
                        </data-label>
                        <data-value>
                            Vul hier de tokens in
                        </data-value>
                    </data-group>
                </flex-container>

                <flex-container
                    :screen-sizes="{
                        laptop: 8,
                    }"
                >
                    <flex-container :size="5">
                        <input-group
                            v-model="form.relatieToken"
                            :schema="requiredValues.relatieToken ? string().required() : string()"
                        >
                            <template #label>
                                <input-label>
                                    Relatie token
                                </input-label>
                            </template>
                            <template #input>
                                <text-input />
                            </template>
                        </input-group>
                    </flex-container>

                    <flex-container :size="2" align-content="flex-end">
                        <button-block @loading="validate(form.relatieToken, 'relaties', $event)">
                            Valideren
                        </button-block>
                    </flex-container>

                    <flex-container :size="5">
                        <input-group
                            v-model="form.agendaToken"
                            :schema="requiredValues.agendaToken ? string().required() : string()"
                        >
                            <template #label>
                                <input-label>
                                    Agenda token
                                </input-label>
                            </template>
                            <template #input>
                                <text-input />
                            </template>
                        </input-group>
                    </flex-container>

                    <flex-container :size="2" align-content="flex-end">
                        <button-block @loading="validate(form.agendaToken, 'agenda', $event)">
                            Valideren
                        </button-block>
                    </flex-container>

                    <flex-container :size="5">
                        <input-group
                            v-model="form.takenToken"
                            :schema="requiredValues.takenToken ? string().required() : string()"
                        >
                            <template #label>
                                <input-label>
                                    Taken token
                                </input-label>
                            </template>
                            <template #input>
                                <text-input />
                            </template>
                        </input-group>
                    </flex-container>

                    <flex-container :size="2" align-content="flex-end">
                        <button-block @loading="validate(form.takenToken, 'taken', $event)">
                            Valideren
                        </button-block>
                    </flex-container>

                    <flex-container :size="5">
                        <input-group
                            v-model="form.wonenToken"
                            :schema="requiredValues.wonenToken ? string().required() : string()"
                        >
                            <template #label>
                                <input-label>
                                    Wonen token
                                </input-label>
                            </template>
                            <template #input>
                                <text-input />
                            </template>
                        </input-group>
                    </flex-container>

                    <flex-container :size="2" align-content="flex-end">
                        <button-block @loading="validate(form.wonenToken, 'objecten', $event)">
                            Valideren
                        </button-block>
                    </flex-container>
                </flex-container>
            </flex-container>
            <div />
            <flex-container
                v-if="realworksTokenModal"
                justify-content="flex-end"
                align-items="center"
            >
                <button-text @click="cancel">
                    Terug
                </button-text>
                <button-block @loading="confirm(); $event.button.setState(ButtonState.completed)">
                    Bevestigen
                </button-block>
            </flex-container>
        </flex-container>
    </modal-dialog>
</template>

<script setup lang="ts">
    import CheckIcon from '@keyvue/one-awesome-icons/icons/regular/check.svg';
    import CopyIcon from '@keyvue/one-awesome-icons/icons/regular/copy.svg';
    import { type ExposedOverlay, FontSize, ButtonState, IconSize, NotificationBlock, useNotify } from '@keyvue/one-ui';
    import { RealworksCredentialsAPI, RealworksCredentials } from '@keyvue/one-api-client-legacy';
    import { ref } from 'vue';
    import { string } from 'yup';
    import { v4 } from 'uuid';
    import useClipboard from '@/shared/composables/useClipboard';

    export interface Form {
        relatieToken: string
        agendaToken: string
        takenToken: string
        wonenToken: string
    }

    export interface ExposedRealworksTokenForm {
        form: Form,
        setForm: (values: Form) => void,
        overlay: ExposedOverlay,
    }

    const { notify } = useNotify();
    const { writeText } = useClipboard();

    const realworksTokenModal = ref<ExposedOverlay>();

    const form = ref<Form>({
        relatieToken: '',
        agendaToken: '',
        takenToken: '',
        wonenToken: '',
    });

    const requiredValues = ref<{
        relatieToken: boolean
        agendaToken: boolean
        takenToken: boolean
        wonenToken: boolean
    }>({
        relatieToken: false,
        agendaToken: false,
        takenToken: false,
        wonenToken: false,
    });

    const emit = defineEmits(['confirmForm', 'cancelForm']);

    async function validate(token: string, type: 'objecten' | 'agenda' | 'taken' | 'relaties', event: any) {
        try {
            const credential = RealworksCredentials.create({
                id: v4(),
                apiKey: token,
                enabled: true,
                createdAt: new Date(),
                updatedAt: new Date(),
                description: 'validation',
            });

            const results = await RealworksCredentialsAPI.validate(credential);

            if (!results.basic && !results[type]) {
                throw Error('Token Validation failed');
            }

            notify({
                component: NotificationBlock,
                props: {
                    title: 'Dit token is geldig!',
                    text: 'Dit realworks token is geldig.',
                    color: 'green-500',
                },
                duration: 4000,
            });

            event.button.setState(ButtonState.completed);

            return true;
        } catch (e) {
            notify({
                component: NotificationBlock,
                props: {
                    title: 'Er is iets foutgegaan!',
                    text: 'Dit realworks token is niet geldig.',
                    color: 'red-500',
                },
                duration: 4000,
            });

            event.button.setState(ButtonState.completed);

            return false;
        }
    }

    function confirm() {
        if (!realworksTokenModal.value) {
            return;
        }

        realworksTokenModal.value.closeOverlay();

        emit('confirmForm');
    }

    function cancel() {
        if (!realworksTokenModal.value) {
            return;
        }

        realworksTokenModal.value.closeOverlay();

        emit('cancelForm');
    }

    function setForm(values: Form) {
        form.value = values;

        requiredValues.value = {
            relatieToken: values.relatieToken !== '',
            agendaToken: values.agendaToken !== '',
            takenToken: values.takenToken !== '',
            wonenToken: values.wonenToken !== '',
        };
    }

    defineExpose({
        overlay: realworksTokenModal,
        setForm,
        form,
    });
</script>

<style lang="scss" scoped>
.realworks-usage-list {
    display: inline-flex;
    flex-direction: row;
    list-style: none;
    gap: size(md);
    margin: 0;

    & > li {
        display: inline-flex;
        flex-direction: row;
        list-style: none;
        gap: size(xs);
    }
}
</style>
